/**
 * First-pass word filter for display names and tributes.
 *
 * This is a blunt instrument on purpose: it runs before anything reaches a
 * human moderator (Phase 4) and its job is to catch the obvious, not to be
 * clever. The server treats it as advisory for tributes (they still queue for
 * approval) and as blocking for display names, which are never reviewed.
 *
 * Ops can extend the lists at runtime via server/config/wordlist.json, which
 * the venue loads at boot and every module loads from its own DATA_DIR. See
 * `extendFilter` — additions only, because the thing that costs something on
 * the night is a list emptied by accident, not one that grew too long.
 */
import { MAX_NAME_LENGTH, MAX_TRIBUTE_LENGTH } from "./constants.js";
/** Matched on word boundaries after normalisation. */
export const BLOCKED_WORDS = [
    // English
    "fuck",
    "fucking",
    "fucker",
    "shit",
    "bullshit",
    "bitch",
    "bastard",
    "asshole",
    "arsehole",
    "dick",
    "cock",
    "cunt",
    "pussy",
    "whore",
    "slut",
    "rape",
    "rapist",
    "nigger",
    "nigga",
    "faggot",
    "retard",
    "retarded",
    "porn",
    "nude",
    "nudes",
    // Hindi / Hinglish (romanised)
    "chutiya",
    "chutiye",
    "madarchod",
    "behenchod",
    "bhenchod",
    "bhosdi",
    "bhosdike",
    "gaand",
    "gandu",
    "harami",
    "kutiya",
    "randi",
    "lund",
    "lauda",
    // Assamese (romanised)
    "bosdi",
    "suwor",
    "beisya",
    "murkho",
];
/**
 * Matched anywhere in the whitespace-stripped string, so "f u c k" and
 * "c-h-u-t-i-y-a" are caught too. Only terms that cannot appear inside a real
 * name belong here — "shit" would reject Nashit, "randi" would reject Brandi.
 */
export const BLOCKED_SUBSTRINGS = [
    "fuck",
    "cunt",
    "nigger",
    "nigga",
    "faggot",
    "rapist",
    "madarchod",
    "behenchod",
    "bhenchod",
    "bhosdike",
    "chutiya",
];
/** Names that would let someone pose as staff or as the artist. */
export const RESERVED_NAMES = [
    "admin",
    "moderator",
    "mod",
    "official",
    "zubeengarg",
    "zubeen",
    "zubeenda",
    "blockwave",
    "system",
    "server",
];
/**
 * Inflections of a blocked stem that are still the same word.
 *
 * The filter matched whole words only, so "bastard" was caught and "bastards"
 * walked through — which matters because display names are *blocked* on this
 * list rather than queued for review. Only plural and possessive endings are
 * generated: "-er" would catch the surname Cocker, and a memorial that refuses
 * someone's real name is a worse failure than one that lets a plural through.
 * Possessives need no rule of their own — the apostrophe is dropped during
 * folding, so "bastard's" arrives here as "bastards".
 */
function inflections(term) {
    const out = [term, `${term}s`, `${term}es`];
    if (term.endsWith("y"))
        out.push(`${term.slice(0, -1)}ies`);
    return out;
}
/**
 * The lists actually in force, which are the shipped ones plus whatever ops
 * added at runtime. Held separately so the exported constants stay the
 * documented baseline and a test can put them back.
 */
let activeWords = [...BLOCKED_WORDS];
let activeSubstrings = [...BLOCKED_SUBSTRINGS];
let activeReserved = new Set(RESERVED_NAMES);
/** Every inflection of every blocked word, so a check is one Set lookup. */
let wordLookup = new Set(activeWords.flatMap(inflections));
/**
 * Add terms to the filter at runtime, for the slur that starts spreading at
 * 9pm on a night when nobody can ship a deploy. Additive and idempotent:
 * nothing shipped can be removed this way, because the failure that costs
 * something here is a moderator accidentally emptying the list mid-event.
 * Terms are folded the same way input is, so ops can paste them in any case.
 */
export function extendFilter(extra) {
    const fold = (list) => (list ?? []).map((t) => normalizeForFilter(t).replace(/\s+/g, "")).filter(Boolean);
    activeWords = [...new Set([...activeWords, ...fold(extra.words)])];
    activeSubstrings = [...new Set([...activeSubstrings, ...fold(extra.substrings)])];
    for (const name of fold(extra.reserved))
        activeReserved.add(name);
    wordLookup = new Set(activeWords.flatMap(inflections));
}
/** Drop every runtime addition and return to the shipped lists. */
export function resetFilter() {
    activeWords = [...BLOCKED_WORDS];
    activeSubstrings = [...BLOCKED_SUBSTRINGS];
    activeReserved = new Set(RESERVED_NAMES);
    wordLookup = new Set(activeWords.flatMap(inflections));
}
/** What the filter is enforcing right now, for /admin and for boot logging. */
export function filterSize() {
    return {
        words: activeWords.length,
        substrings: activeSubstrings.length,
        reserved: activeReserved.size,
    };
}
const LEET = {
    "0": "o",
    "1": "i",
    "3": "e",
    "4": "a",
    "5": "s",
    "7": "t",
    "@": "a",
    $: "s",
    "!": "i",
    "|": "i",
};
/** Letters we keep when folding: Latin, Devanagari, Bengali/Assamese, spaces. */
// eslint-disable-next-line no-misleading-character-class
const ALLOWED_CHAR = /[a-z\u0900-\u097F\u0980-\u09FF\s]/u;
// Control, zero-width and bidi-override characters are stripped on purpose:
// they are the classic trick for smuggling an invisible or reversed name.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001F\u007F-\u009F\u200B-\u200F\u202A-\u202E\uFEFF]/g;
/**
 * Fold a string down to a comparable form: lowercase, de-accented, leetspeak
 * expanded, punctuation dropped, runs of a repeated letter collapsed.
 */
export function normalizeForFilter(input) {
    const lowered = input.toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "");
    let out = "";
    for (const ch of lowered) {
        const mapped = LEET[ch] ?? ch;
        if (ALLOWED_CHAR.test(mapped))
            out += mapped;
    }
    return out
        .replace(/(.)\1{2,}/g, "$1$1")
        .replace(/\s+/g, " ")
        .trim();
}
/** True when the text trips the first-pass filter. */
export function containsProfanity(input) {
    const normalized = normalizeForFilter(input);
    if (!normalized)
        return false;
    const collapsed = normalized.replace(/\s+/g, "");
    for (const term of activeSubstrings) {
        if (collapsed.includes(term))
            return true;
    }
    // Walk the input rather than the list: a tribute is a handful of words, the
    // list is hundreds of stems once inflected.
    for (const word of normalized.split(" ")) {
        if (wordLookup.has(word))
            return true;
    }
    return false;
}
/**
 * Clean and validate a display name. Trims, collapses whitespace, strips
 * control characters, then applies length, reserved-name and profanity rules.
 */
export function sanitizeName(raw) {
    const value = raw.replace(CONTROL_CHARS, "").replace(/\s+/g, " ").trim();
    if (!value)
        return { ok: false, value: "", reason: "empty" };
    if (value.length > MAX_NAME_LENGTH)
        return { ok: false, value: "", reason: "too_long" };
    if (!/[\p{L}\p{N}]/u.test(value))
        return { ok: false, value: "", reason: "invalid_chars" };
    const normalized = normalizeForFilter(value).replace(/\s+/g, "");
    if (activeReserved.has(normalized))
        return { ok: false, value: "", reason: "reserved" };
    if (containsProfanity(value))
        return { ok: false, value: "", reason: "profanity" };
    return { ok: true, value };
}
/**
 * Clean and screen a tribute before it enters the moderation queue.
 *
 * A pass here is not approval — it only means a human is worth troubling.
 * Everything that survives this still waits for a moderator (Phase 4), which
 * is the actual guarantee that nothing unmoderated reaches the wall.
 */
export function sanitizeTribute(raw) {
    const value = raw
        .replace(CONTROL_CHARS, "")
        .replace(/[\r\n\t]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    if (!value)
        return { ok: false, value: "", reason: "empty" };
    if (value.length > MAX_TRIBUTE_LENGTH)
        return { ok: false, value: "", reason: "too_long" };
    if (!/[\p{L}\p{N}]/u.test(value))
        return { ok: false, value: "", reason: "invalid_chars" };
    if (containsProfanity(value))
        return { ok: false, value: "", reason: "filtered" };
    return { ok: true, value };
}
//# sourceMappingURL=moderation.js.map